import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import axios from "axios";
import Chart from "react-apexcharts";

import { endPoint } from "../../global";
import { useAuth } from "../../context/AuthProvider";
import Loading from "../../components/Loading";
import ResponseAlert from "../../components/ResponseAlert";

const poppins = {
  fontFamily: "'Poppins', sans-serif",
};

const chartColors = ["#f59e0b", "#cbd5e1", "#fbbf24", "#a78bfa"];

const gradientColors = ["#fbbf24", "#e2e8f0", "#fde68a", "#c4b5fd"];

const pageWrapper = {
  width: "100%",
  minHeight: "100%",
  zIndex: 1,
  position: "relative",
  px: { xs: 2, md: 3 },
  py: { xs: 2, md: 2 },
};

const glassPanel = {
  position: "relative",
  overflow: "hidden",
  borderRadius: "20px",
  background:
    "linear-gradient(145deg, rgba(8,22,51,0.78), rgba(15,45,92,0.48))",
  border: "1px solid rgba(147,197,253,0.24)",
  backdropFilter: "blur(18px)",
  WebkitBackdropFilter: "blur(18px)",
  boxShadow: "0 16px 42px rgba(0,0,0,0.3)",
};

const statCard = {
  ...glassPanel,
  p: 1.45,
  minHeight: 88,
};

const chartPanel = {
  ...glassPanel,
  p: { xs: 1.5, md: 1.8 },
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    background:
      "radial-gradient(circle at top right, rgba(96,165,250,0.16), transparent 34%)",
    pointerEvents: "none",
  },
};

const sidePanel = {
  ...glassPanel,
  p: 1.8,
};

const chartBox = {
  mt: 1.6,
  borderRadius: "16px",
  p: { xs: 1, md: 1.2 },
  background:
    "linear-gradient(145deg, rgba(3,10,26,0.58), rgba(15,35,78,0.38))",
  border: "1px solid rgba(147,197,253,0.14)",
  boxShadow: "inset 0 0 35px rgba(96,165,250,0.08)",
};

const iconBox = {
  width: 38,
  height: 38,
  borderRadius: "13px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "rgba(96,165,250,0.16)",
  border: "1px solid rgba(147,197,253,0.28)",
  color: "#93c5fd",
  flexShrink: 0,
};

const getPackageColor = (name, index = 0) => {
  const lowerName = String(name || "").toLowerCase();

  if (lowerName.includes("bronze")) return "#f59e0b";
  if (lowerName.includes("silver")) return "#cbd5e1";
  if (lowerName.includes("gold")) return "#fbbf24";
  if (lowerName.includes("platinum")) return "#a78bfa";

  return chartColors[index % chartColors.length];
};

const getPurchaseCount = (item) => {
  if (!item) return 0;

  return Number(item.purchaseCount) || 0;
};

const getPackageQuantity = (item) => {
  if (!item) return 0;

  return Number(item.quantity) || 0;
};

const StatCard = ({ icon, label, value, helper, color }) => (
  <Box sx={statCard}>
    <Box
      sx={{
        position: "relative",
        zIndex: 1,
        display: "flex",
        alignItems: "center",
        gap: 1.2,
      }}
    >
      <Box sx={iconBox}>{icon}</Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            ...poppins,
            color: "rgba(219,234,254,0.64)",
            fontSize: "0.68rem",
            mb: 0.35,
          }}
        >
          {label}
        </Typography>

        <Typography
          sx={{
            ...poppins,
            color: color || "#fff",
            fontSize: { xs: "1.25rem", md: "1.45rem" },
            fontWeight: 900,
            lineHeight: 1.1,
            textShadow: "0 0 18px rgba(96,165,250,0.25)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {value}
        </Typography>

        <Typography
          sx={{
            ...poppins,
            color: "rgba(255,255,255,0.52)",
            fontSize: "0.64rem",
            mt: 0.35,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {helper}
        </Typography>
      </Box>
    </Box>
  </Box>
);

export default function Dashboard() {
  const { token } = useAuth();

  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState("");
  const [success, setSuccess] = useState(true);
  const [openAlert, setOpenAlert] = useState(false);

  const [dataProduct, setDataProduct] = useState([]);
  const [dataPackage, setDataPackage] = useState([]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);

      try {
        const { data } = await axios.get(`${endPoint}dashboard`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        setDataProduct(data.product ?? []);
        setDataPackage(data.package ?? []);
      } catch (error) {
        setOpenAlert(true);
        setSuccess(false);
        setResponse(error.response?.data?.msg ?? "Terjadi kesalahan.");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();

    const interval = setInterval(fetchDashboardData, 5000);

    return () => clearInterval(interval);
  }, [token]);

  const packageCategories = useMemo(
    () => dataPackage.map((item) => item.name),
    [dataPackage],
  );

  const packageQuantities = useMemo(
    () => dataPackage.map((item) => getPackageQuantity(item)),
    [dataPackage],
  );

  const sortedPackageByQuantity = useMemo(() => {
    return [...dataPackage].sort(
      (a, b) => getPackageQuantity(b) - getPackageQuantity(a),
    );
  }, [dataPackage]);

  const sortedPackageByPurchase = useMemo(() => {
    return [...dataPackage].sort(
      (a, b) => getPurchaseCount(b) - getPurchaseCount(a),
    );
  }, [dataPackage]);

  const totalProduct = useMemo(() => {
    return dataProduct.reduce(
      (total, item) => total + (Number(item.quantity) || 0),
      0,
    );
  }, [dataProduct]);

  const totalUnitFromPackage = useMemo(() => {
    return dataPackage.reduce(
      (total, item) => total + getPackageQuantity(item),
      0,
    );
  }, [dataPackage]);

  const totalPurchase = useMemo(() => {
    return dataPackage.reduce(
      (total, item) => total + getPurchaseCount(item),
      0,
    );
  }, [dataPackage]);

  const mainProductName = dataProduct[0]?.name ?? "Solartuff";

  const bestPackageByQuantity = sortedPackageByQuantity[0];
  const bestPackageByPurchase = sortedPackageByPurchase[0];

  const barOptions = useMemo(
    () => ({
      chart: {
        id: "package-quantity-chart",
        toolbar: {
          show: false,
        },
        background: "transparent",
        foreColor: "rgba(255,255,255,0.82)",
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: "42%",
          borderRadius: 9,
          borderRadiusApplication: "end",
          distributed: true,
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (value) => `${value}`,
        offsetY: -18,
        style: {
          fontSize: "11px",
          fontFamily: "Poppins, sans-serif",
          fontWeight: 900,
          colors: ["#fff"],
        },
        dropShadow: {
          enabled: true,
          blur: 4,
          opacity: 0.5,
        },
      },
      xaxis: {
        categories: packageCategories,
        labels: {
          rotate: -10,
          trim: true,
          style: {
            fontSize: "10.5px",
            fontFamily: "Poppins, sans-serif",
            fontWeight: 700,
            colors: "rgba(255,255,255,0.78)",
          },
        },
        axisBorder: {
          color: "rgba(147,197,253,0.2)",
        },
        axisTicks: {
          color: "rgba(147,197,253,0.2)",
        },
      },
      yaxis: {
        labels: {
          style: {
            colors: "rgba(219,234,254,0.65)",
            fontFamily: "Poppins, sans-serif",
            fontSize: "10.5px",
          },
        },
      },
      grid: {
        borderColor: "rgba(147,197,253,0.12)",
        strokeDashArray: 5,
      },
      colors: dataPackage.map((item, index) =>
        getPackageColor(item.name, index),
      ),
      fill: {
        type: "gradient",
        gradient: {
          shade: "dark",
          type: "vertical",
          shadeIntensity: 0.35,
          gradientToColors: gradientColors,
          opacityFrom: 1,
          opacityTo: 0.82,
          stops: [0, 100],
        },
      },
      tooltip: {
        theme: "dark",
        y: {
          formatter: (value, options) => {
            const item = dataPackage[options.dataPointIndex];
            const count = getPurchaseCount(item);

            return `${value} unit • ${count}x pembelian`;
          },
        },
      },
      legend: {
        show: false,
      },
    }),
    [packageCategories, dataPackage],
  );

  const barSeries = useMemo(
    () => [
      {
        name: "Jumlah Unit",
        data: packageQuantities,
      },
    ],
    [packageQuantities],
  );

  return (
    <Box sx={pageWrapper}>
      <Box sx={{ mb: 1.7 }}>
        <Typography
          sx={{
            ...poppins,
            fontWeight: 900,
            fontSize: { xs: "1.4rem", md: "1.7rem" },
            color: "#fff",
            lineHeight: 1.15,
            textShadow: "0 0 22px rgba(96,165,250,0.45)",
          }}
        >
          Dashboard Penjualan
        </Typography>

        <Typography
          sx={{
            ...poppins,
            fontWeight: 400,
            fontSize: "0.78rem",
            color: "rgba(219,234,254,0.72)",
            mt: 0.4,
          }}
        >
          Ringkasan unit terjual dan jumlah pembelian paket
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(3, 1fr)",
          },
          gap: 1.3,
          mb: 1.8,
        }}
      >
        <StatCard
          icon={<Inventory2OutlinedIcon />}
          label="Total Unit Terjual"
          value={totalProduct || totalUnitFromPackage}
          helper={`Unit ${mainProductName}`}
          color="#6ee7b7"
        />

        <StatCard
          icon={<ShoppingBagOutlinedIcon />}
          label="Total Pembelian Paket"
          value={totalPurchase}
          helper="Jumlah transaksi paket"
          color="#93c5fd"
        />

        <StatCard
          icon={<EmojiEventsOutlinedIcon />}
          label="Paket Terfavorit"
          value={bestPackageByPurchase?.name ?? "-"}
          helper={`${getPurchaseCount(bestPackageByPurchase)}x pembelian`}
          color={getPackageColor(bestPackageByPurchase?.name)}
        />
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            lg: "1.45fr 0.55fr",
          },
          gap: 1.8,
          alignItems: "stretch",
        }}
      >
        <Box sx={chartPanel}>
          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.1 }}>
              <Box sx={iconBox}>
                <BarChartOutlinedIcon />
              </Box>

              <Box>
                <Typography
                  sx={{
                    ...poppins,
                    fontWeight: 900,
                    fontSize: "0.95rem",
                    color: "#fff",
                    lineHeight: 1.2,
                  }}
                >
                  Unit Terjual per Paket
                </Typography>

                <Typography
                  sx={{
                    ...poppins,
                    fontSize: "0.68rem",
                    color: "rgba(219,234,254,0.62)",
                    mt: 0.2,
                  }}
                >
                  Total {totalProduct || totalUnitFromPackage} unit dari seluruh
                  paket
                </Typography>
              </Box>
            </Box>

            <Box sx={chartBox}>
              <Chart
                options={barOptions}
                series={barSeries}
                type="bar"
                width="100%"
                height={275}
              />
            </Box>
          </Box>
        </Box>

        <Box sx={sidePanel}>
          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.1 }}>
              <Box sx={iconBox}>
                <EmojiEventsOutlinedIcon />
              </Box>

              <Box>
                <Typography
                  sx={{
                    ...poppins,
                    fontWeight: 900,
                    fontSize: "0.95rem",
                    color: "#fff",
                  }}
                >
                  Ranking Paket
                </Typography>

                <Typography
                  sx={{
                    ...poppins,
                    fontSize: "0.68rem",
                    color: "rgba(219,234,254,0.62)",
                  }}
                >
                  Berdasarkan unit terjual
                </Typography>
              </Box>
            </Box>

            <Box sx={{ mt: 1.4 }}>
              {sortedPackageByQuantity.map((item, index) => {
                const purchase = getPurchaseCount(item);
                const quantity = getPackageQuantity(item);
                const percentage = totalUnitFromPackage
                  ? Math.round((quantity / totalUnitFromPackage) * 100)
                  : 0;

                return (
                  <Box key={item.packageId} sx={{ mb: 1.15 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 1,
                        mb: 0.45,
                      }}
                    >
                      <Typography
                        sx={{
                          ...poppins,
                          fontSize: "0.72rem",
                          fontWeight: 800,
                          color: "#fff",
                        }}
                      >
                        #{index + 1} {item.name}
                      </Typography>

                      <Typography
                        sx={{
                          ...poppins,
                          fontSize: "0.72rem",
                          fontWeight: 900,
                          color: getPackageColor(item.name, index),
                        }}
                      >
                        {quantity}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        height: 7,
                        borderRadius: 999,
                        background: "rgba(255,255,255,0.1)",
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        sx={{
                          width: `${percentage}%`,
                          height: "100%",
                          borderRadius: 999,
                          background: getPackageColor(item.name, index),
                          boxShadow: "0 0 18px rgba(96,165,250,0.45)",
                        }}
                      />
                    </Box>

                    <Typography
                      sx={{
                        ...poppins,
                        mt: 0.3,
                        color: "rgba(219,234,254,0.52)",
                        fontSize: "0.62rem",
                      }}
                    >
                      {percentage}% dari total unit • {purchase}x pembelian
                    </Typography>
                  </Box>
                );
              })}

              {!sortedPackageByQuantity.length && (
                <Typography
                  sx={{
                    ...poppins,
                    color: "rgba(219,234,254,0.58)",
                    fontSize: "0.78rem",
                  }}
                >
                  Belum ada data paket.
                </Typography>
              )}
            </Box>

            <Box
              sx={{
                mt: 1.2,
                p: 1.15,
                borderRadius: "14px",
                background: "rgba(255,255,255,0.07)",
                border: "1px solid rgba(147,197,253,0.16)",
              }}
            >
              <Typography
                sx={{
                  ...poppins,
                  color: "rgba(219,234,254,0.6)",
                  fontSize: "0.66rem",
                  mb: 0.25,
                }}
              >
                Qty tertinggi
              </Typography>

              <Typography
                sx={{
                  ...poppins,
                  color: "#fff",
                  fontWeight: 900,
                  fontSize: "0.9rem",
                }}
              >
                {bestPackageByQuantity?.name ?? "-"} •{" "}
                {getPackageQuantity(bestPackageByQuantity)} unit
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      <Loading open={loading} />

      <ResponseAlert
        response={response}
        success={success}
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
      />
    </Box>
  );
}
